import { useEffect, useState } from 'react';
import {
  useCreateHolidayMutation,
  useDeleteHolidayMutation,
  useGetLocationQuery,
  useSetBusinessHoursMutation,
  useUpdateLocationMutation
} from '@/store/configApi';
import { useActiveLocation } from '@/hooks/useActiveLocation';
import { useAppDispatch } from '@/store';
import { pushToast } from '@/store/uiSlice';
import { LocationPicker } from '@/components/layout/LocationPicker';
import { Button, EmptyState, Field, Panel, SkeletonRows } from '@/components/ui';
import { defaultWeek, emptyWeek, HoursEditor, type DayHours } from '@/components/forms/HoursEditor';

export function HoursPage() {
  const { locationId } = useActiveLocation();
  const dispatch = useAppDispatch();

  const location = useGetLocationQuery(locationId, { skip: !locationId });
  const [saveHours, savingHours] = useSetBusinessHoursMutation();
  const [updateLocation, savingLocation] = useUpdateLocationMutation();

  const [createHoliday, creatingHoliday] = useCreateHolidayMutation();
  const [deleteHoliday] = useDeleteHolidayMutation();

  const [days, setDays] = useState<DayHours[]>(emptyWeek());
  const [transferNumber, setTransferNumber] = useState('');
  const [holiday, setHoliday] = useState({ name: '', date: '', message: '' });

  useEffect(() => {
    if (!location.data) return;

    const loaded = emptyWeek();
    for (const day of location.data.hours ?? []) {
      loaded[day.weekday] = {
        weekday: day.weekday,
        closed: day.closed,
        windows: day.windows ?? []
      };
    }
    setDays(loaded.some((day) => !day.closed) ? loaded : defaultWeek());
    setTransferNumber(location.data.transferNumber ?? '');
  }, [location.data]);

  const persistHours = async () => {
    const result = await saveHours({ id: locationId, days });
    dispatch(
      'error' in result
        ? pushToast('Could not save your hours.', 'error')
        : pushToast('Hours saved. Callers hear the new times right away.', 'success')
    );
  };

  const persistTransfer = async () => {
    const result = await updateLocation({ id: locationId, transferNumber });
    dispatch(
      'error' in result
        ? pushToast('Could not save that number.', 'error')
        : pushToast('Transfer number saved.', 'success')
    );
  };

  const addHoliday = async () => {
    if (!holiday.name.trim() || !holiday.date) {
      dispatch(pushToast('A name and a date are both needed.', 'error'));
      return;
    }

    const result = await createHoliday({
      locationId,
      name: holiday.name.trim(),
      date: holiday.date,
      closed: true,
      message: holiday.message.trim() || undefined
    });

    if ('error' in result) {
      dispatch(pushToast('Could not save that closure.', 'error'));
      return;
    }
    setHoliday({ name: '', date: '', message: '' });
    dispatch(pushToast('Closure saved.', 'success'));
  };

  const removeHoliday = async (holidayId: string) => {
    const result = await deleteHoliday({ locationId, holidayId });
    dispatch(
      'error' in result
        ? pushToast('Could not remove that closure.', 'error')
        : pushToast('Closure removed.', 'success')
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-display">Hours</h1>
        <LocationPicker />
      </div>

      <Panel title="Opening hours">
        {location.isLoading ? (
          <SkeletonRows rows={7} />
        ) : (
          <div className="px-4 py-4">
            <p className="mb-4 max-w-2xl text-small text-slate">
              Callers who ring outside these hours are told you are closed and given the
              times below. Changing them updates what the line says immediately.
            </p>
            <HoursEditor
              value={days}
              onChange={setDays}
              onSave={persistHours}
              saving={savingHours.isLoading}
            />
          </div>
        )}
      </Panel>

      <Panel title="Days you are closed">
        <div className="px-4 py-4">
          <p className="mb-4 max-w-2xl text-small text-slate">
            Holidays and one-off closures override your usual hours. Callers on those days
            hear your message instead of the standard closed announcement.
          </p>

          {(location.data?.holidays.length ?? 0) === 0 ? (
            <EmptyState
              headline="No closures set"
              hint="Add the dates you know about now, so nobody is told you are open when you are not."
            />
          ) : (
            <ul className="divide-y divide-rule border-y border-rule">
              {location.data?.holidays.map((entry) => (
                <li key={entry._id} className="flex flex-wrap items-center gap-3 py-2.5">
                  <span className="tabular text-small text-ink">{entry.date}</span>
                  <span className="text-small text-ink">{entry.name}</span>
                  {entry.message ? (
                    <span className="text-small text-slate">{entry.message}</span>
                  ) : null}
                  <button
                    onClick={() => removeHoliday(entry._id)}
                    className="ml-auto text-small text-slate hover:text-rust"
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="mt-4 grid gap-3 sm:grid-cols-[1fr_1fr]">
            <Field
              label="What is the day?"
              value={holiday.name}
              onChange={(event) => setHoliday({ ...holiday, name: event.target.value })}
              placeholder="Christmas Day"
            />
            <Field
              label="Date"
              type="date"
              value={holiday.date}
              onChange={(event) => setHoliday({ ...holiday, date: event.target.value })}
            />
          </div>

          <div className="mt-3 space-y-1.5">
            <label htmlFor="holidayMessage" className="block text-small font-medium text-ink">
              What callers hear that day
            </label>
            <input
              id="holidayMessage"
              value={holiday.message}
              onChange={(event) => setHoliday({ ...holiday, message: event.target.value })}
              placeholder="We are closed for Christmas and back on the 27th."
              className="w-full rounded border border-rule bg-white px-3 py-2 text-small"
            />
          </div>

          <Button className="mt-3" loading={creatingHoliday.isLoading} onClick={addHoliday}>
            Add closure
          </Button>
        </div>
      </Panel>

      <Panel title="When the line cannot help">
        <div className="max-w-md space-y-3 px-4 py-4">
          <Field
            label="Transfer callers to"
            value={transferNumber}
            onChange={(event) => setTransferNumber(event.target.value)}
            placeholder="416 555 0100"
          />
          <p className="text-small text-slate">
            Used when a caller asks for a person, or when the line hears something twice that
            it was not set up for. Without it, those callers are politely turned away.
          </p>
          <Button
            variant="secondary"
            loading={savingLocation.isLoading}
            onClick={persistTransfer}
          >
            Save number
          </Button>
        </div>
      </Panel>
    </div>
  );
}
